import { z } from 'zod';
import { EditableField } from './EditableField';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Row from 'components/Layout/Row';
import Box from '@mui/material/Box';
import { match } from 'ts-pattern';
import { Controller, useForm } from 'react-hook-form';
import CurrencySelector from 'components/CurrencySelector';
import BasicSelect from 'further-ui/components/BasicSelect';
import useFeeTemplateOptions from 'hooks/lp/data/fee-manager/useFeeTemplateOptions';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Close from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';

const FixedWidthBox = styled(Box)(() => ({
  width: '300px',
}));

const schema = z.object({
  [EditableField.InvestorCurrency]: z.nativeEnum(CurrencyCode).optional(),
  [EditableField.FeeTemplateId]: z.string(),
  [EditableField.CarriedInterestFeeTemplateId]: z.string(),
  [EditableField.EqualisationInterestFeeTemplateId]: z.string(),
});

type FormData = z.infer<typeof schema>;

export type OutputData = Omit<
  FormData,
  | 'feeTemplateId'
  | 'carriedInterestFeeTemplateId'
  | 'equalisationInterestFeeTemplateId'
> & {
  feeTemplate?: { id: string; name: string } | null;
  carriedInterestFeeTemplate?: { id: string; name: string } | null;
  equalisationInterestFeeTemplate?: { id: string; name: string } | null;
};

type Props = {
  vehicleId: string;
  editedField: EditableField;
  onClose: () => void;
  onSave: (data: OutputData) => void;
} & FormData;

const EditableFieldsRow: React.FC<Props> = ({
  vehicleId,
  editedField,
  investorCurrency,
  feeTemplateId,
  carriedInterestFeeTemplateId,
  equalisationInterestFeeTemplateId,
  onClose,
  onSave,
}) => {
  const lpFeeTemplates = useFeeTemplateOptions(vehicleId, true);
  const { vehicle } = useVehicle(vehicleId);

  const defaultValues: FormData = {
    investorCurrency,
    feeTemplateId,
    carriedInterestFeeTemplateId,
    equalisationInterestFeeTemplateId,
  };

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleClose = () => {
    form.reset(defaultValues);
    onClose();
  };

  const handleSave = () => {
    form.handleSubmit(
      ({
        feeTemplateId,
        equalisationInterestFeeTemplateId,
        carriedInterestFeeTemplateId,
        investorCurrency,
      }) => {
        onSave({
          investorCurrency,
          feeTemplate:
            feeTemplateId && feeTemplateId !== 'none'
              ? {
                  id: feeTemplateId,
                  name:
                    lpFeeTemplates.find(
                      (feeTemplate) => feeTemplate.value === feeTemplateId,
                    )?.label || '',
                }
              : null,
          equalisationInterestFeeTemplate:
            equalisationInterestFeeTemplateId &&
            equalisationInterestFeeTemplateId !== 'none'
              ? {
                  id: equalisationInterestFeeTemplateId,
                  name:
                    vehicle?.equalisationSettings.equalisationInterestFeeTemplates.find(
                      (feeTemplate) =>
                        feeTemplate._id === equalisationInterestFeeTemplateId,
                    )?.name || '',
                }
              : null,
          carriedInterestFeeTemplate:
            carriedInterestFeeTemplateId &&
            carriedInterestFeeTemplateId !== 'none'
              ? {
                  id: carriedInterestFeeTemplateId,
                  name:
                    vehicle?.carrySettings.carriedInterestFeeTemplates.find(
                      (feeTemplate) =>
                        feeTemplate._id === carriedInterestFeeTemplateId,
                    )?.name || '',
                }
              : null,
        });
      },
    )();
    onClose();
  };

  return (
    <Row spacing="sm" centered fullWidth>
      <FixedWidthBox>
        {match(editedField)
          .with(EditableField.InvestorCurrency, () => (
            <Controller
              control={form.control}
              name="investorCurrency"
              render={({ field }) => (
                <CurrencySelector
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          ))
          .with(EditableField.FeeTemplateId, () => (
            <Controller
              control={form.control}
              name="feeTemplateId"
              render={({ field }) => (
                <BasicSelect
                  fullWidth
                  size="small"
                  placeholder="Select template"
                  value={field.value}
                  onChange={field.onChange}
                  options={lpFeeTemplates}
                  noOptionsMessage="No fee templates available"
                />
              )}
            />
          ))
          .with(EditableField.CarriedInterestFeeTemplateId, () => (
            <Controller
              control={form.control}
              name="carriedInterestFeeTemplateId"
              render={({ field }) => (
                <BasicSelect
                  fullWidth
                  size="small"
                  placeholder="Select template"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'none', label: 'None' },
                    ...(vehicle?.carrySettings.carriedInterestFeeTemplates.map(
                      (template) => ({
                        label: template.name,
                        value: template._id,
                      }),
                    ) ?? []),
                  ]}
                  noOptionsMessage="No fee templates available"
                />
              )}
            />
          ))
          .with(EditableField.EqualisationInterestFeeTemplateId, () => (
            <Controller
              control={form.control}
              name="equalisationInterestFeeTemplateId"
              render={({ field }) => (
                <BasicSelect
                  fullWidth
                  size="small"
                  placeholder="Select template"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'none', label: 'None' },
                    ...(vehicle?.equalisationSettings.equalisationInterestFeeTemplates.map(
                      (template) => ({
                        label: template.name,
                        value: template._id,
                      }),
                    ) ?? []),
                  ]}
                  noOptionsMessage="No fee templates available"
                />
              )}
            />
          ))
          .otherwise(() => (
            <Controller
              control={form.control}
              name={editedField}
              render={({ field }) => (
                <TextField
                  fullWidth
                  size="small"
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          ))}
      </FixedWidthBox>
      <Button onClick={handleSave} variant="contained" color="primary">
        Save
      </Button>
      <ButtonBase onClick={handleClose}>
        <Close fontSize="small" />
      </ButtonBase>
    </Row>
  );
};

export default EditableFieldsRow;
